import React, { useEffect } from 'react';

import { useMatch, Navigate, Route, Routes } from 'react-router-dom';

// import WalletsDetails from '@pages/WalletsDetails';
// import Deposit from '@src/pages2/Deposit';
import Limits from '@src/pages2/Limits';
import OperationWithNewCard from '@src/pages2/OperationWithNewCard';
// import Sell from '@src/pages2/Sell';
import SendToBankAccount from '@src/pages2/SendToBankAccount';
// import Topup from '@src/pages2/Topup';
import TopupVerifyCard from '@src/pages2/TopupVerifyCard';
// import WithdrawCrypto from '@src/pages2/WithdrawCrypto';
import AccountsBuy from '@src/pages2/accounts/AccountsBuy';
import AccountsDetails from '@src/pages2/accounts/AccountsDetails';
import AccountsIntro from '@src/pages2/accounts/AccountsIntro';
import AccountsMain from '@src/pages2/accounts/AccountsMain';
import AccountsPaymentDetails from '@src/pages2/accounts/AccountsPaymentDetails';
import AccountsSell from '@src/pages2/accounts/AccountsSell';
import AccountsSend from '@src/pages2/accounts/AccountsSend';
import AccountsTopup from '@src/pages2/accounts/AccountsTopup';
import AccountsVerification from '@src/pages2/accounts/AccountsVerification';
import Intro from '@src/pages2/protect/Protect2FA/Intro';
import Setup from '@src/pages2/protect/Protect2FA/Setup';
import Success from '@src/pages2/protect/Protect2FA/Success';
import Verify2FACode from '@src/pages2/protect/Protect2FA/Verify2FACode';
import { currencyPath, handleLocalStorage } from '@utils';
import { useInterval, useNotifications, useNavigate } from '@utils/hooks';

import {
  cardsActions,
  globalActions,
  loginActions,
  userActions,
  useStore,
  walletActions,
} from '@modules';
import { AccountCreateStatus, PaymentAliasEnum } from '@modules/accounts/types';
import { useChatContext } from '@modules/support';
import { ProtectNextEnum } from '@modules/types';
import { IbanStatusesEnum, VerificationStatusesEnum } from '@modules/user/types';

import BindVerifyCardStatus from '@containers/BindVerifyCardStatus';
import ContainerModals from '@containers/ContainerModals';
import VerifyCard from '@containers/VerifyCard';
import AccountSendLayoutOld from '@layouts/AccountSendLayoutOld';
import AccountTopupLayoutOld from '@layouts/AccountTopupLayoutOld';
import LoaderLayout from '@layouts/LoaderLayout';
import MainLayout from '@layouts/MainLayout';
import WalletSendLayoutOld from '@layouts/WalletSendLayoutOld';
import WalletTopupLayoutOld from '@layouts/WalletTopupLayoutOld';
import AddCard from '@pages/AddCard';
import Balance from '@pages/Balance';
import BillingAddress from '@pages/BillingAddress';
import ChangePassword from '@pages/ChangePassword';
import ChangePhone from '@pages/ChangePhone';
import Invite from '@pages/Invite';
import NewEmail from '@pages/NewEmail';
import OldEmail from '@pages/OldEmail';
import Portfolio from '@pages/Portfolio';
import Profile from '@pages/Profile';
import ProfileEdit from '@pages/ProfileEdit';
import ProfileOverview from '@pages/ProfileOverview';
import SettingsEmail from '@pages/SettingsEmail';
import SettingsPassword from '@pages/SettingsPassword';
import SettingsPhone from '@pages/SettingsPhone';
import Verification from '@pages/Verification';
import VerifyDescriptor from '@pages/VerifyDescriptor';
import VerifyIdentity from '@pages/VerifyIdentity';
import VerifyPassword from '@pages/VerifyPassword';
import WalletsDetails from '@pages/WalletsDetails';
import KYCBlocked from '@pages/user/KYCBlocked';
import UserExists from '@pages/user/UserExists';

import useDefaultRedirect from './useDefaultRedirect';

const { failed, under_review, complete, incomplete } = VerificationStatusesEnum;
const { available } = IbanStatusesEnum;
const { success, fail } = AccountCreateStatus;
const { setupOTP } = ProtectNextEnum;

const { getItem } = handleLocalStorage;

const PrivateRoute: CustomFC = () => {
  const [{ login, user, accounts }, dispatch] = useStore();
  const navigate = useNavigate();
  const { isLoggedIn, protectNext } = login;
  const { data, loaders } = user;
  const { createAccountStatus } = accounts;
  const isLoading = !!loaders.getUserData || !!loaders.getUserDataPolling;
  const { isOpen } = useChatContext();

  const isIbanAvailable = data?.iban_status === available;
  const documentStatus = data?.checks.document;
  const documentInProgress = documentStatus === under_review;

  const documentProofOfAddressInProgress = data?.checks.proof_of_address === under_review;
  const documentProofOfAddressIncomplete = data?.checks.proof_of_address === incomplete;

  const {
    showErrorOpenAccount,
    showSuccessOpenAccount,
    showErrorPOANotification,
    showSuccessPOANotification,
  } = useNotifications();

  const keepAliveCallback = () => dispatch(loginActions.keepAlive());
  const getUserDataCallback = () => dispatch(userActions.getUserDataPolling());

  useDefaultRedirect();

  const isAccountsRoute = useMatch(CONST.nav.accounts);

  useEffect(() => {
    if (createAccountStatus === success) showSuccessOpenAccount();
    if (createAccountStatus === fail) showErrorOpenAccount();
  }, [createAccountStatus]);

  const getUserDataAndShowPOAStatus = () => {
    dispatch(userActions.getUserDataPolling())
      .unwrap()
      .then(res => {
        if (!isAccountsRoute) {
          if (res.data?.checks.proof_of_address === failed) showErrorPOANotification();
          if (res.data?.checks.proof_of_address === complete) showSuccessPOANotification();
        }
      });
  };

  const handleRedirect = () => {
    dispatch(loginActions.logout());
    dispatch(userActions.clearErrors());
    dispatch(loginActions.clearErrors());
    navigate(CONST.nav.error);
  };

  // KYC1 pooling
  useInterval({
    callback: getUserDataCallback,
    shouldStop: isLoading || !documentInProgress || !documentProofOfAddressIncomplete,
    delay: CONST.KYCStatusInterval,
  });

  // KYC2 pooling
  useInterval({
    callback: getUserDataAndShowPOAStatus,
    shouldStop: isLoading || isIbanAvailable || !documentProofOfAddressInProgress,
    delay: CONST.KYCStatusInterval,
  });

  useEffect(() => {
    if (isOpen && login.isLoggedIn) keepAliveCallback();
  }, [isOpen]);

  useEffect(() => {
    if (isLoggedIn) {
      const isStrongModal = !!getItem('isStrongModal');
      dispatch(userActions.zendeskVerification())
        .unwrap()
        .then(res => {
          if (res.data.token) {
            window.zE('messenger', 'loginUser', callback => {
              callback(res.data.token);
            });
          }
        })
        .catch(err => err);
      dispatch(userActions.getUserData())
        .unwrap()
        .catch(() => handleRedirect());
      dispatch(userActions.getZHDisclaimer());
      dispatch(userActions.setStrongPasswordModal(isStrongModal));
      dispatch(cardsActions.getCards());
      dispatch(walletActions.getWallet());
      dispatch(globalActions.getCurrencies());
      dispatch(globalActions.getCountries());
      if (protectNext === setupOTP) navigate(CONST.nav.intro2fa);
    }
  }, [isLoggedIn]);

  if (!isLoggedIn) return <Navigate to={CONST.nav.login} replace />;

  return (
    <LoaderLayout isLoading={!user.data}>
      <Routes>
        {/* Wallets - Topup */}
        <Route path={CONST.nav.verifyDescriptor} element={<VerifyDescriptor />} />
        <Route
          path={currencyPath(CONST.nav.walletsBuyAccountsExchange)}
          element={
            <WalletTopupLayoutOld>
              <AccountsBuy paymentAlias={PaymentAliasEnum.iban_exchange} />
            </WalletTopupLayoutOld>
          }
        />
        <Route
          path={currencyPath(CONST.nav.walletsBuyAccountsInvoice)}
          element={
            <WalletTopupLayoutOld>
              <AccountsBuy paymentAlias={PaymentAliasEnum.iban_invoice} />
            </WalletTopupLayoutOld>
          }
        />
        <Route path={CONST.nav.verifyCard} element={<TopupVerifyCard />} />
        <Route path={`${CONST.nav.operationWithNewCard}/*`} element={<OperationWithNewCard />} />

        {/* Wallets - Send */}
        <Route
          path={currencyPath(CONST.nav.walletsWithdrawAccountsExchange)}
          element={
            <WalletSendLayoutOld>
              <AccountsSell paymentAlias={PaymentAliasEnum.iban_exchange} />
            </WalletSendLayoutOld>
          }
        />
        <Route path={CONST.nav.sendToBankAccount} element={<SendToBankAccount />} />

        {/* Wallets */}
        <Route
          path={CONST.nav.limits}
          element={<Navigate to={`${CONST.nav.limits}/${CONST.defaultCryptoCurrency}`} replace />}
        />
        <Route path={currencyPath(CONST.nav.limits)} element={<Limits />} />

        {/* Accounts - Modals */}
        {/* <Route path={CONST.nav.accountsIntro} element={<AccountsIntro />} />
        <Route
          path={CONST.nav.accountsVerification}
          element={
            <MainLayout>
              <AccountsVerification />
            </MainLayout>
          }
        /> */}

        {/* Accounts - Topup */}
        <Route
          path={CONST.nav.accountsTopupWalletsExchange}
          element={
            <AccountTopupLayoutOld>
              <AccountsSell paymentAlias={PaymentAliasEnum.iban_exchange} />
            </AccountTopupLayoutOld>
          }
        />
        <Route path={CONST.nav.accountsTopup} element={<AccountsTopup />} />

        {/* Accounts - Send */}
        <Route
          path={CONST.nav.accountsSendWalletsExchange}
          element={
            <AccountSendLayoutOld>
              <AccountsBuy paymentAlias={PaymentAliasEnum.iban_exchange} />
            </AccountSendLayoutOld>
          }
        />
        <Route path={`${CONST.nav.accountsSend}/*`} element={<AccountsSend />} />

        {/* Accounts */}
        <Route path={CONST.nav.accounts} element={<AccountsMain />} />
        <Route path={CONST.nav.accountsDetails} element={<AccountsDetails />} />
        <Route path={CONST.nav.accountsBuyInvoiceDetails} element={<AccountsPaymentDetails />} />

        {/* Invite */}
        <Route path={currencyPath(CONST.nav.invite)} element={<Invite />} />

        {/* Profile */}
        <Route path={CONST.nav.profileOverview} element={<ProfileOverview />} />
        <Route path={CONST.nav.verifyIdentity} element={<VerifyIdentity />} />
        <Route path={CONST.nav.profileEdit} element={<ProfileEdit />} />
        <Route path={CONST.nav.profileBillingAddress} element={<BillingAddress />} />
        {/* TODO: Create common layout for 8 pages below */}
        <Route path={CONST.nav.profile} element={<Profile />} />
        <Route path={CONST.nav.profileSetPhone} element={<SettingsPhone />} />
        <Route path={CONST.nav.profileChangePhone} element={<ChangePhone />} />
        <Route path={CONST.nav.profileSetEmail} element={<SettingsEmail />} />
        <Route path={CONST.nav.profileOldEmail} element={<OldEmail />} />
        <Route path={CONST.nav.profileNewEmail} element={<NewEmail />} />
        <Route path={CONST.nav.profileNewEmailVerifyPassword} element={<VerifyPassword />} />
        <Route path={CONST.nav.profileSetPassword} element={<SettingsPassword />} />
        <Route path={CONST.nav.profileChangePassword} element={<ChangePassword />} />
        <Route
          path={CONST.nav.profileCardsAdd}
          element={
            <BindVerifyCardStatus>
              <AddCard />
            </BindVerifyCardStatus>
          }
        />
        <Route
          path={CONST.nav.profileCardsVerify}
          element={
            <BindVerifyCardStatus onSuccess={() => navigate(CONST.nav.profile)}>
              <MainLayout backButton={() => navigate(CONST.nav.profileCardsAdd)}>
                <VerifyCard onSuccessFlow={() => navigate(CONST.nav.profile)} />
              </MainLayout>
            </BindVerifyCardStatus>
          }
        />

        {/* TODO: Fix common verification layouts */}
        <Route path={CONST.nav.verification} element={<Verification />} />

        {/* Other */}
        <Route path={CONST.nav.kycBlocked} element={<KYCBlocked />} />
        <Route path={CONST.nav.accountExists} element={<UserExists />} />

        <Route path={currencyPath(CONST.nav.balance)} element={<Balance />} />
        <Route path={CONST.nav.wallets} element={<Portfolio />} />

        <Route path={CONST.nav.intro2fa} element={<Intro />} />
        <Route path={CONST.nav.setup2fa} element={<Setup />} />
        <Route path={CONST.nav.success2fa} element={<Success />} />
        <Route path={CONST.nav.verify2fa} element={<Verify2FACode />} />

        {/* <Route path={CONST.nav.purchase} element={<Purchase />} /> */}
        <Route path={CONST.nav.balances} element={<WalletsDetails />} />
        {/* <Route path={CONST.nav.profileLimits} element={<ProfileLimits />} /> */}
        {/* <Route path={CONST.nav.verificationIncomplete} element={<VerificationIncomplete />} /> */}
        {/* <Route path={CONST.nav.verificationInProgress} element={<VerificationInProgress />} /> */}

        <Route path="*" element={<Navigate to={CONST.nav.wallets} replace />} />
      </Routes>
      <ContainerModals />
    </LoaderLayout>
  );
};

export default PrivateRoute;
